// npm
import React, { memo, useRef } from 'react'
import { Box, Container, alpha, styled } from '@mui/material'

import SwiperCore, {
  Autoplay,
  Navigation,
  Pagination,
  EffectFade,
} from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/effect-fade'
import { Swiper, SwiperSlide } from 'swiper/react'

// src
import DirectionButton from '@atoms/buttons/DirectionButton'

const SwiperContainer = styled(Box)(({ theme }) => ({
  ['.quotes-swiper']: {
    position: 'relative',
  },
  ['.swiper-pagination-bullets.swiper-pagination-horizontal']: {
    width: 'auto',
  },
  ['.swiper-pagination-bullet']: {
    width: '8px',
    height: '8px',
    background: 'rgba(255,255,255,0.7)',
    opacity: 1,
    position: 'relative',
    margin: '0 10px !important' as any,
    ['&:before']: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate3d(-50%,-50%,0)',
      content: '""',
      display: 'block',
      width: '22px',
      height: '22px',
      background: 'none',
      borderRadius: '50%',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: alpha(theme.palette.primary.main, 0),
      transition: theme.transitions.create(['border-color']),
    },
    ['&.swiper-pagination-bullet-active']: {
      ['&:before']: {
        borderColor: alpha(theme.palette.primary.main, 0.8),
      },
    },
  },
}))

const NavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  marginTop: theme.spacing(-12),
  marginBottom: theme.spacing(4),
  flexDirection: 'row',
}))

export type slide = {
  id: string
  children?: React.ReactNode
}

interface Props {
  slides: slide[]
  children?: React.ReactNode
  withNav?: boolean
}

const BgImageSwiper = ({ slides, children, withNav }: Props) => {
  const prevRef = withNav ? useRef(null) : undefined
  const nextRef = withNav ? useRef(null) : undefined

  withNav
    ? SwiperCore.use([Autoplay, Pagination, Navigation, EffectFade])
    : SwiperCore.use([Autoplay, EffectFade])

  return (
    <SwiperContainer>
      <Swiper
        onInit={(swiper) => {
          if (withNav) {
            // @ts-ignore
            swiper.params.navigation.prevEl = prevRef.current
            // @ts-ignore
            swiper.params.navigation.nextEl = nextRef.current
            swiper.navigation.init()
            swiper.navigation.update()
          }
        }}
        pagination={{
          el: '.swiper-pagination-container',
        }}
        slidesPerView={1}
        observer={true}
        loop={true}
        spaceBetween={0}
        observeParents={true}
        autoHeight={true}
        autoplay={{
          delay: 5000,
        }}
        effect="fade"
        speed={1000}
        className="quotes-swiper"
      >
        {slides.map((slide: slide) => {
          console.log('slid id: ', slide.id)
          return <SwiperSlide key={slide.id}>{slide.children}</SwiperSlide>
        })}
        {(withNav || children) && (
          <Container
            maxWidth="xl"
            sx={
              children
                ? {
                    position: 'absolute',
                    zIndex: 20,
                    inset: 0,
                  }
                : { position: 'relative', zIndex: 20 }
            }
          >
            {children}

            {withNav && (
              <NavContainer>
                <DirectionButton
                  direction="left"
                  aria-label="previous"
                  ref={prevRef}
                />
                <Box mx={1}>
                  <div className="swiper-pagination-container" />
                </Box>
                <DirectionButton
                  direction="right"
                  aria-label="next"
                  ref={nextRef}
                />
              </NavContainer>
            )}
          </Container>
        )}
      </Swiper>
    </SwiperContainer>
  )
}

export default memo(BgImageSwiper)
